import { useState } from 'react';
import { Link } from 'react-router-dom';
import './CollectionPage.css';
import { douches, robinetsMuraux, robinetsAPoser, robinetsSuspendus, robinetsWC, allRobinetterie } from '../data/robinetterie';
import { vasquesMarbre, vasquesLaiton, allVasques } from '../data/vasques';
import { bougeoirs, etagereDouche, miroirs, porteSavons, porteServiettes, porteBrosses, portesPapierToilette, allAccessoires } from '../data/accessoires';
import IMAGES from '../images';

/* ─── Lightbox ─────────────────────────────────────────────── */
function Lightbox({ images, startIndex, onClose }) {
  const [current, setCurrent] = useState(startIndex);
  const prev = (e) => { e.stopPropagation(); setCurrent((c) => (c - 1 + images.length) % images.length); };
  const next = (e) => { e.stopPropagation(); setCurrent((c) => (c + 1) % images.length); };
  const handleKey = (e) => {
    if (e.key === 'ArrowLeft') prev(e);
    if (e.key === 'ArrowRight') next(e);
    if (e.key === 'Escape') onClose();
  };
  return (
    <div className="lightbox" onClick={onClose} onKeyDown={handleKey} tabIndex={0} ref={el => el && el.focus()}>
      <button className="lightbox__close" onClick={onClose}>✕</button>
      <button className="lightbox__arrow lightbox__arrow--prev" onClick={prev}>‹</button>
      <div className="lightbox__img-wrap" onClick={(e) => e.stopPropagation()}>
        <img src={images[current]} alt={`Vue ${current + 1}`} className="lightbox__img" />
        <div className="lightbox__counter">{current + 1} / {images.length}</div>
      </div>
      <button className="lightbox__arrow lightbox__arrow--next" onClick={next}>›</button>
    </div>
  );
}

/* ─── Product Editorial Block ──────────────────────────────── */
function ProductBlock({ product }) {
  const [lightbox, setLightbox] = useState(null);
  const isSingle = !product.images || product.images.length === 1;

  return (
    <div className="cpage__product-block">
      <div className="cpage__product-info">
        {product.collection && (
          <span className="cpage__product-collection">{product.collection}</span>
        )}
        <h2 className="cpage__product-name">{product.name}</h2>
        {product.material && (
          <p className="cpage__product-material">{product.material}</p>
        )}
        <div className="cpage__product-divider" />
      </div>

      <div className={`cpage__product-gallery ${isSingle ? 'cpage__product-gallery--single' : ''}`}>
        {product.images && product.images.map((imgUrl, idx) => (
          <div
            key={idx}
            className="cpage__product-thumb-wrap"
            onClick={() => setLightbox(idx)}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={imgUrl}
              alt={`${product.name} - Vue ${idx + 1}`}
              className="cpage__product-thumb"
            />
          </div>
        ))}
      </div>

      <div className="cpage__product-action">
        <Link to="/contact" className="cpage__product-btn">Demander un Devis</Link>
      </div>

      {lightbox !== null && product.images && (
        <Lightbox
          images={product.images}
          startIndex={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

/* ─── Generic Collection Page ──────────────────────────────── */
export function CollectionPage({ title, label, description, products }) {
  return (
    <main className="cpage">
      <div className="cpage__banner">
        <img src={products[0]?.img} alt={title} className="cpage__banner-img" />
        <div className="cpage__banner-overlay" />
        <div className="cpage__banner-content">
          <span className="section-label">{label}</span>
          <h1 className="cpage__banner-title">{title}</h1>
          <div className="cpage__banner-divider" />
          {description && <p className="cpage__banner-desc">{description}</p>}
        </div>
      </div>

      <section className="cpage__content-section">
        <div className="cpage__products-list">
          {products.map((p, i) => <ProductBlock key={i} product={p} />)}
        </div>
      </section>

      <div className="cpage__cta-strip">
        <p>Une création sur mesure ? Contactez-nous pour un projet personnalisé.</p>
        <Link to="/contact" className="btn-outline">Nous Contacter</Link>
      </div>
    </main>
  );
}

/* ─── Robinetterie ─────────────────────────────────────────── */
export function Robinetterie() {
  return (
    <CollectionPage
      title="Robinetterie"
      label="Savoir-faire Artisanal"
      description="Chaque robinet est façonné à la main en laiton massif, alliant robustesse et élégance intemporelle."
      products={allRobinetterie}
    />
  );
}
export function RobinetterieSub({ sub }) {
  const map = {
    douches: {
      title: 'Douches',
      desc: 'Des douches au design épuré, façonnées en laiton massif pour offrir une expérience de douche luxueuse et intemporelle.',
      data: douches
    },
    muraux: {
      title: 'Robinets Muraux',
      desc: 'Élégance minimaliste et gain d\'espace absolu avec nos robinets encastrés aux finitions précieuses.',
      data: robinetsMuraux
    },
    poser: {
      title: 'Robinets à Poser',
      desc: 'De véritables sculptures fonctionnelles posées délicatement pour magnifier votre espace vasque.',
      data: robinetsAPoser
    },
    suspendus: {
      title: 'Robinets Suspendus',
      desc: 'Une arrivée d\'eau aérienne et spectaculaire qui réinvente l\'espace avec audace et légèreté.',
      data: robinetsSuspendus
    },
    leaf: {
      title: 'Robinet Suspendu Leaf',
      desc: 'La poésie d\'une feuille de laiton suspendue, où le design rencontre la délicatesse de la nature.',
      data: robinetsSuspendus.filter(p => p.name.includes('Leaf'))
    },
    wc: {
      title: 'Robinets WC',
      desc: 'Le soin des finitions artisanales jusque dans les moindres détails de vos espaces d\'eau intimes.',
      data: robinetsWC
    },
  };
  const { title, desc, data } = map[sub] || map.douches;
  return (
    <CollectionPage
      title={title}
      label="Robinetterie"
      description={desc}
      products={data}
    />
  );
}

/* ─── Vasques ──────────────────────────────────────────────── */
export function Vasques() {
  return (
    <CollectionPage
      title="Vasques"
      label="Matières Nobles"
      description="Sculptées dans les plus belles matières, nos vasques transforment chaque salle de bain en espace d'exception."
      products={allVasques}
    />
  );
}
export function VasquesSub({ sub }) {
  const map = {
    marbre: {
      title: 'Vasques en Marbre',
      desc: 'Taillées dans des blocs de marbre brut rigoureusement sélectionnés, nos vasques célèbrent la noblesse de la pierre naturelle.',
      data: vasquesMarbre
    },
    laiton: {
      title: 'Vasques en Laiton Martelé',
      desc: 'Chaque pièce est forgée à la main par nos maîtres artisans, offrant des reflets dorés texturés et vibrants.',
      data: vasquesLaiton
    },
  };
  const { title, desc, data } = map[sub] || map.marbre;
  return (
    <CollectionPage
      title={title}
      label="Vasques"
      description={desc}
      products={data}
    />
  );
}

/* ─── Accessoires ──────────────────────────────────────────── */
export function Accessoires() {
  const entretienProducts = IMAGES.entretien.map((url, i) => ({
    name: `Produit d'Entretien ${i + 1}`,
    img: url,
    images: [url],
    material: 'Soin du Laiton',
    collection: 'Entretien',
  }));
  return (
    <CollectionPage
      title="Accessoires"
      label="Compléments d'Excellence"
      description="Des accessoires de salle de bain pensés dans la continuité de nos collections, pour une cohérence absolue."
      products={[...allAccessoires, ...entretienProducts]}
    />
  );
}
export function AccessoiresSub({ sub }) {
  const map = {
    'porte-savons': {
      title: 'Porte-Savons',
      desc: 'L\'accessoire fonctionnel élevé au rang d\'objet d\'art pour accueillir vos rituels de soin.',
      data: porteSavons
    },
    'porte-serviettes': {
      title: 'Porte-Serviettes',
      desc: 'Des lignes épurées et géométriques en laiton massif pour suspendre vos plus beaux linges.',
      data: porteServiettes
    },
    miroirs: {
      title: 'Miroirs',
      desc: 'Des cadres dorés façonnés avec art pour refléter et agrandir l\'élégance de votre salle de bain.',
      data: miroirs
    },
    bougeoirs: {
      title: 'Bougeoirs',
      desc: 'Une douce chaleur portée par le laiton sculpté, sublimant l\'ambiance de vos espaces.',
      data: bougeoirs
    },
    'porte-brosses': {
      title: 'Porte-Brosses',
      desc: 'La continuité esthétique dans chaque recoin, transformant le fonctionnel en sublime.',
      data: porteBrosses
    },
    'papier-toilette': {
      title: 'Portes Papier Toilette',
      desc: 'Un raffinement discret et une finition impeccable pour une harmonie décorative totale.',
      data: portesPapierToilette
    },
    'etagere-douche': {
      title: 'Étagère de Douche',
      desc: 'L\'alliance parfaite de la robustesse et du design chic pour organiser vos essentiels de bain.',
      data: etagereDouche
    },
  };
  const { title, desc, data } = map[sub] || {
    title: 'Accessoires',
    desc: 'Des compléments d\'exception en laiton sculpté.',
    data: allAccessoires
  };
  return (
    <CollectionPage
      title={title}
      label="Accessoires"
      description={desc}
      products={data}
    />
  );
}
